import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BookMarked, ChevronLeft, Plus, Search, Trash2 } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchGlossary,
  createGlossary,
  deleteGlossary,
  LOC_STR as S,
  type GlossaryTermRow,
} from './localizationExtraApi'

/**
 * A22.13 — Glossariy. Domen-atamalarining afzal tarjimasi (izchillik uchun).
 * Endi ADMIN-boshqariladigan JONLI jadval (GET/POST/DELETE
 * /admin/localization/glossary) — avvalgi statik massiv o'rniga. Tarjimonlar bir
 * xil atamani ishlatsin. Qo'shish/o'chirish serverga yoziladi (audited).
 */
export function LocalizationGlossary() {
  const t = useT()
  const { toast } = useToast()
  const [q, setQ] = useState('')
  const [terms, setTerms] = useState<GlossaryTermRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((x) => x + 1), [])

  const [en, setEn] = useState('')
  const [ru, setRu] = useState('')
  const [uz, setUz] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchGlossary(ac.signal)
      .then(setTerms)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const rows = useMemo(() => {
    const list = terms ?? []
    const s = q.trim().toLowerCase()
    if (!s) return list
    return list.filter((x) => `${x.en} ${x.ru} ${x.uz}`.toLowerCase().includes(s))
  }, [q, terms])

  const submit = async () => {
    if (!en.trim()) return
    setBusy(true)
    try {
      await createGlossary({ en: en.trim(), ru: ru.trim(), uz: uz.trim(), note: note.trim() })
      setEn('')
      setRu('')
      setUz('')
      setNote('')
      toast(S.termAdded, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (row: GlossaryTermRow) => {
    try {
      await deleteGlossary(row.id)
      toast(S.termRemoved, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={t.loc.glossaryTitle}
        screenId="A22.13"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <p className="mb-2 text-sm text-secondary">{t.loc.glossaryNote}</p>
        {/* Atama qo'shish */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <input
            value={en}
            onChange={(e) => setEn(e.target.value)}
            placeholder="EN"
            className="w-32 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          <input
            value={ru}
            onChange={(e) => setRu(e.target.value)}
            placeholder="RU"
            className="w-32 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          <input
            value={uz}
            onChange={(e) => setUz(e.target.value)}
            placeholder="UZ"
            className="w-32 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={S.notePh}
            className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || !en.trim()}
            className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            <Plus size={14} />
            {S.addTerm}
          </button>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
          <Search size={14} className="text-tertiary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.loc.searchPh}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
          <span className="whitespace-nowrap text-xs text-tertiary">
            {rows.length} / {terms?.length ?? 0}
          </span>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !terms ? (
          <div className="space-y-1.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border-default">
            <div className="grid grid-cols-[1fr_1fr_1fr_1.4fr_auto] gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
              <span>EN</span>
              <span>RU</span>
              <span>UZ</span>
              <span>{t.loc.namespace}</span>
              <span className="w-6" />
            </div>
            {rows.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-tertiary">
                <BookMarked size={22} className="mx-auto mb-2 opacity-60" />
                {t.states.emptyTitle}
              </div>
            ) : (
              rows.map((x) => (
                <div
                  key={x.id}
                  className="grid grid-cols-[1fr_1fr_1fr_1.4fr_auto] items-center gap-3 border-b border-border-subtle px-3 py-1.5 text-sm last:border-0"
                >
                  <span className="font-medium text-primary">{x.en}</span>
                  <span className="text-secondary">{x.ru}</span>
                  <span className="text-secondary">{x.uz}</span>
                  <span className="truncate text-xs text-tertiary" title={x.note}>
                    {x.note || '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(x)}
                    title={S.remove}
                    className="text-tertiary hover:text-danger"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  )
}
